import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { Share2, Loader2 } from 'lucide-react';
import { LotusOm, OrnDivider } from './Ornaments';
import { WhatsAppGlyph } from './CtaButtons';
import { SITE_URL, WHATSAPP_NUMBER } from '../../lib/constants';
import { track } from '../../lib/analytics';

const BlessingCard = ({ name, seva }) => {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const firstName = (name || 'Devotee').split(' ')[0];

  const shareImage = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: '#5C1616',
        useCORS: true,
        logging: false,
      });
      const blob = await new Promise((res) => canvas.toBlob(res, 'image/png'));
      const file = new File([blob], 'omvedha-sai-baba-blessing.png', { type: 'image/png' });
      track('cta_share', 'blessing-card');
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'Om Sai Ram',
          text: 'May Sai Baba bless you and your family.',
        });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'omvedha-sai-baba-blessing.png';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      // ignore — user can still use WhatsApp share
    } finally {
      setBusy(false);
    }
  };

  const shareWhatsApp = () => {
    const msg = `Om Sai Ram. I have offered ${seva} at the Gurupurnima Sai Baba Mahotsava (29 July 2026 · RajaRajeshwari Nagar, Bangalore) through Omvedha. May Sai Baba bless you and your family with peace, prosperity and good health. Join the celebrations: ${SITE_URL}`;
    track('cta_share', 'blessing-whatsapp');
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="mt-8 w-full" data-testid="blessing-card-wrap">
      <p className="mb-4 text-center font-sans text-xs uppercase tracking-[0.18em] text-gold-deep">
        Your Blessing Card
      </p>

      {/* Card to capture */}
      <div
        ref={cardRef}
        data-testid="blessing-card"
        className="relative mx-auto max-w-md overflow-hidden rounded-3xl bg-maroon px-8 py-10 text-center text-ivory"
        style={{ boxShadow: '0 30px 70px -40px rgba(92,22,22,0.7)' }}
      >
        <div
          className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.22), transparent 70%)' }}
        />
        <div className="relative flex flex-col items-center">
          <LotusOm size={56} />
          <p className="mt-4 font-serif text-2xl text-gold">Om Sai Ram</p>
          <div className="my-4">
            <OrnDivider tone="#D4AF37" />
          </div>
          <p className="font-sans text-sm text-ivory/80">With devotion,</p>
          <p className="mt-1 font-serif text-2xl text-ivory">{firstName}</p>
          <p className="mt-3 font-sans text-sm leading-relaxed text-ivory/80">
            has offered <span className="font-semibold text-gold">{seva}</span> at the
          </p>
          <p className="mt-1 font-serif text-lg text-gold">Gurupurnima Sai Baba Mahotsava</p>
          <p className="font-sans text-xs text-ivory/70">29 July 2026 · RajaRajeshwari Nagar, Bangalore</p>
          <p className="mt-5 font-serif text-base italic leading-relaxed text-ivory/90">
            “May Sai Baba bless you and your family with peace, prosperity, health and protection.”
          </p>
          <p className="mt-6 font-serif text-lg font-semibold text-gold">Omvedha</p>
          <p className="font-sans text-[10px] uppercase tracking-[0.2em] text-ivory/50">
            www.omvedha.com
          </p>
        </div>
      </div>

      {/* Share actions (not captured) */}
      <div className="mt-5 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <button
          type="button"
          data-testid="blessing-share-image"
          onClick={shareImage}
          disabled={busy}
          className="inline-flex items-center gap-2.5 rounded-full px-6 py-3 font-sans text-xs font-semibold uppercase tracking-[0.15em] text-ink disabled:opacity-60"
          style={{ background: 'linear-gradient(180deg,#F5D97A,#D4AF37 45%,#B38B22)' }}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
          {busy ? 'Preparing…' : 'Share / Save Card'}
        </button>
        <button
          type="button"
          data-testid="blessing-share-whatsapp"
          onClick={shareWhatsApp}
          className="inline-flex items-center gap-2.5 rounded-full bg-whatsapp px-6 py-3 font-sans text-xs font-semibold uppercase tracking-[0.15em] text-white"
        >
          <WhatsAppGlyph className="h-4 w-4" /> Share on WhatsApp
        </button>
      </div>
    </div>
  );
};

export default BlessingCard;
